import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Paper,
  makeStyles,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import { getEligibilityStats } from '../../services/eligibilityService';
import { Severity } from '../../types';
import useSnackbar from '../../hooks/useSnackbar';
import { EligibilityStats } from './EligibilityTypes';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
  LineChart,
  Line,
} from 'recharts';
import { useAuthContext } from 'contexts/authContext';
import TenantSelector from '../../components/TenantSelector';
import PeriodSelector from '../../components/PeriodSelector/PeriodSelector'; 

const useStyles = makeStyles((theme) => ({
  statsContainer: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1a1f36',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
  },
  totalNumber: {
    fontSize: 48,
    fontWeight: 500,
    textAlign: 'center',
    margin: theme.spacing(2, 0),
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.8)',
  },
  chartCard: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#1a1f36',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    minHeight: 400,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    color: 'white',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: 200,
  },
}));

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        style={{
          padding: '10px',
          border: '1px solid #ccc',
          backgroundColor: '#1a1f36',
          color: 'white',
        }}
      >
        <Typography variant="body2">{`${label}: ${payload[0].value}`}</Typography>
      </Paper>
    );
  }
  return null;
};

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

export const EligibilityStatsComponent: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [statsData, setStatsData] = useState<EligibilityStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedPeriod, setSelectedPeriod] = useState<string | number>('last-12-months');
  const [selectedCarrier, setSelectedCarrier] = useState<number>(0);
  const show = useSnackbar();
  const { user } = useAuthContext();

  const fetchStatsData = useCallback(async () => {
    try {
      setLoading(true);
      const stats = await getEligibilityStats(
        selectedPeriod,
        selectedCarrier || undefined
      );
      setStatsData(stats);
    } catch (error) {
      show('Failed to load eligibility statistics', Severity.error);
    } finally {
      setLoading(false);
    }
  }, [show, selectedPeriod, selectedCarrier]);

  useEffect(() => {
    fetchStatsData();
  }, [selectedPeriod, selectedCarrier]);

  // Prepare data for monthly charts
  const getTripDaysPerMonthChartData = () => {
    if (!statsData) return [];
    return monthNames.map((name, index) => {
      const monthData = statsData.tripDaysPerMonth.find(
        (m) => m.month === index + 1
      );
      return {
        name,
        value: monthData ? monthData.days : 0,
      };
    });
  };

  const getMembersPerMonthChartData = () => {
    if (!statsData) return [];
    return monthNames.map((name, index) => {
      const monthData = statsData.membersPerMonth.find(
        (m) => m.month === index + 1
      );
      return {
        name,
        value: monthData ? monthData.count : 0,
      };
    });
  };

  const getMembersPerProductChartData = () => {
    if (!statsData) return [];

    // Take top 10 products
    return statsData.membersPerProduct.slice(0, 10).map((product) => ({
      name: product.productName,
      value: product.count,
    }));
  };

  const { role } = user || { role: '' };

  const getPeriodLabel = () => {
    if (selectedPeriod === 'last-12-months') return t('Last 12 Months');
    if (selectedPeriod === 'last-24-months') return t('Last 24 Months');
    if (typeof selectedPeriod === 'number') return selectedPeriod;
    if (typeof selectedPeriod === 'string' && selectedPeriod.startsWith('year-')) {
       return selectedPeriod.replace('year-', '');
    }
    return selectedPeriod;
  };

  return (
    <Box className={classes.statsContainer}>
      {/* Year and Carrier Selectors */}
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        mb={3}
      >
        {role === 'super_user' && (
           <Box mr={2}>
            <TenantSelector 
              value={selectedCarrier}
              onChange={setSelectedCarrier}
            />
          </Box>
        )}
        <PeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
      </Box>

      <Grid container spacing={3}>
        {/* Total Insurance Days */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper className={classes.statCard}>
            <Typography className={classes.title}>
              {t('Total Insurance Days')}
            </Typography>
            <Typography className={classes.totalNumber}>
              {loading
                ? '...'
                : formatNumber(statsData?.totalInsuranceDays || 0)}
            </Typography>
          </Paper>
        </Grid>

        {/* Total Short-Term Policies */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper className={classes.statCard}>
            <Typography className={classes.title}>
              {t('Short-Term Policies')}
            </Typography>
            <Typography className={classes.totalNumber}>
              {loading
                ? '...'
                : formatNumber(statsData?.totalShortTermPolicies || 0)}
            </Typography>
          </Paper>
        </Grid>

        {/* Total Long-Term Policies */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper className={classes.statCard}>
            <Typography className={classes.title}>
              {t('Long-Term Policies')}
            </Typography>
            <Typography className={classes.totalNumber}>
              {loading
                ? '...'
                : formatNumber(statsData?.totalLongTermPolicies || 0)}
            </Typography>
          </Paper>
        </Grid>

        {/* Average Trip Duration */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper className={classes.statCard}>
            <Typography className={classes.title}>
              {t('Average Trip Duration')}
            </Typography>
            <Typography className={classes.totalNumber}>
              {loading ? '...' : `${statsData?.averageTripDuration || 0} days`}
            </Typography>
          </Paper>
        </Grid>

        {/* Trip Days per Month Chart (Short-term policies) */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartCard}>
            <Typography className={classes.chartTitle}>
              {t('Trip Days per Month')} ({t('Short-Term Policies')}) -{' '}
              {getPeriodLabel()}
            </Typography>
            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={40} color="primary" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={getTripDaysPerMonthChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333a56" />
                  <XAxis dataKey="name" stroke="#fff" />
                  <YAxis stroke="#fff" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={{ fill: '#8884d8', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Members per Month Chart */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartCard}>
            <Typography className={classes.chartTitle}>
              {t('Total Members per Month')} - {getPeriodLabel()}
            </Typography>
            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={40} color="primary" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={getMembersPerMonthChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333a56" />
                  <XAxis dataKey="name" stroke="#fff" />
                  <YAxis stroke="#fff" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#4caf50"
                    strokeWidth={2}
                    dot={{ fill: '#4caf50', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Members per Product Chart */}
        <Grid item xs={12}>
          <Paper className={classes.chartCard}>
            <Typography className={classes.chartTitle}>
              {t('Members per Product')} ({t('Top 10')})
            </Typography>
            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={40} color="primary" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={getMembersPerProductChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333a56" />
                  <XAxis
                    dataKey="name"
                    stroke="#fff"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis stroke="#fff" allowDecimals={false} />
                  <Bar dataKey="value" fill="#ff9800" barSize={60}>
                    <LabelList dataKey="value" position="top" fill="#fff" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
